export interface ApiErrorBody {
  error: string
  code?: string
  details?: { path: string; message: string }[] | unknown
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.error)
    this.name = 'ApiError'
  }

  /** Field-level messages, keyed by form field, for react-hook-form to display. */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.body.details)) return {}
    const entries = this.body.details
      .filter((d): d is { path: string; message: string } => typeof d?.path === 'string')
      .map((d) => [d.path, d.message] as const)
    return Object.fromEntries(entries)
  }
}

const BASE = '/api/v1'

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      // Session cookie is first-party, but fetch still needs telling for same-origin.
      credentials: 'include',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    })
  } catch {
    // A dropped connection throws a bare TypeError. Callers all read
    // error.body.error, so reshaping it here is what turns an unreachable
    // server into a visible message instead of a button that does nothing.
    throw new ApiError(0, {
      error: 'Could not reach the server. Check your connection and try again.',
      code: 'NETWORK',
    })
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => ({ error: response.statusText }))

  if (!response.ok) throw new ApiError(response.status, payload as ApiErrorBody)

  return payload as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Builds a query string, dropping empty values so URLs stay readable. */
export function queryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const entry of value) if (entry !== '' && entry != null) search.append(key, String(entry))
    } else {
      search.set(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}
